import React, { useState } from "react";
import { IconGlobe, IconTrash, IconArrowRight, IconGift, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { Gift, Product, Promo } from "../../../types/types";
import ListView from "../../../components/common/ListView";
import { T } from "../../../context/LanguageContext";
import ToggleButton from "../../../components/ToggleButton";

interface ProductCardProps {
  product: Product;
  compact?: boolean;
  onEdit?: (product: Product) => void;
  onView?: (product: Product) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onDelete?: (id: string) => void;
  promos?: Promo[];
  gifts?: Gift[];
  onEditPromo?: (promo: Promo) => void;
  onEditGift?: (gift: Gift) => void;
  onTogglePromoStatus?: (id: string, currentStatus: string) => void;
  onToggleGiftStatus?: (id: string, currentStatus: string) => void;
}

const tabs = [
  {
    label: "Variants",
  },
  {
    label: "Promo",
  },
  {
    label: "Gift",
  },
];

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  compact = false,
  onEdit,
  onView,
  onToggleStatus,
  onDelete,
  promos = [],
  gifts = [],
  onEditPromo,
  onEditGift,
  onTogglePromoStatus,
  onToggleGiftStatus,
}) => {
  const isActive = (product.status || "ACTIVE").toUpperCase() === "ACTIVE";
  const [activeTab, setActiveTab] = useState("Variants");
  const [isExpanded, setIsExpanded] = useState(false);

  const productSkus = React.useMemo(() => {
    return [
      product.sku,
      product.id,
      ...(product.variants?.map((v: any) => v.sku) || []),
      ...(product.variants?.map((v: any) => v.id) || []),
    ].filter(Boolean);
  }, [product]);

  const matchedPromos = React.useMemo(() => {
    if (!promos || !Array.isArray(promos)) return [];
    return promos.filter((p: any) =>
      p.products?.some((item: any) => {
        const itemSku = (item.productSku || item.sku || item.productId || "").toString();
        const itemPid = (item.productId || "").toString();
        return (
          productSkus.some((s) => s.toString().toLowerCase() === itemSku.toLowerCase()) ||
          productSkus.some((s) => s.toString().toLowerCase() === itemPid.toLowerCase())
        );
      })
    );
  }, [promos, productSkus]);

  const matchedGifts = React.useMemo(() => {
    if (!gifts || !Array.isArray(gifts)) return [];
    return gifts.filter((g: any) =>
      productSkus.some(
        (s) =>
          s.toString().toLowerCase() === (g.mainProductSku || "").toString().toLowerCase() ||
          s.toString().toLowerCase() === (g.giftProductSku || "").toString().toLowerCase()
      )
    );
  }, [gifts, productSkus]);
  const customsFields = React.useMemo(() => {
    if (!product.compliance) return { hasHsCode: false, hasOrigin: false, hasDestination: false, hasRoute: false, hasDuty: false, hasVat: false, hasLicense: false, hasDocuments: false, hasAny: false };
    const c = product.compliance;
    const hasHsCode = Boolean(c.hsCode && String(c.hsCode).trim() !== "" && String(c.hsCode) !== "—");
    const hasOrigin = Boolean(c.origin && String(c.origin).trim() !== "" && String(c.origin) !== "—");
    const hasDestination = Boolean(c.destination && String(c.destination).trim() !== "" && String(c.destination) !== "—");
    const hasRoute = hasOrigin || hasDestination;
    const hasDuty = Boolean(
      c.duty !== undefined &&
      c.duty !== null &&
      String(c.duty).trim() !== "" &&
      String(c.duty) !== "0" &&
      String(c.duty) !== "0.00" &&
      String(c.duty) !== "—"
    );
    const hasVat = Boolean(
      c.vat !== undefined &&
      c.vat !== null &&
      String(c.vat).trim() !== "" &&
      String(c.vat) !== "0" &&
      String(c.vat) !== "0.00" &&
      String(c.vat) !== "15" &&
      String(c.vat) !== "15.00" &&
      String(c.vat) !== "—"
    );
    const hasLicense = Boolean(
      c.license &&
      String(c.license).trim() !== "" &&
      String(c.license) !== "Not Required" &&
      String(c.license) !== "—"
    );
    const hasDocuments = Boolean(
      c.documents &&
      String(c.documents).trim() !== "" &&
      String(c.documents) !== "—"
    );
    const hasAny = hasHsCode || hasRoute || hasDuty || hasVat || hasLicense || hasDocuments;
    return { hasHsCode, hasOrigin, hasDestination, hasRoute, hasDuty, hasVat, hasLicense, hasDocuments, hasAny };
  }, [product.compliance]);

  const hasStorage = React.useMemo(() => {
    if (!product.storage) return false;
    if (Array.isArray(product.storage)) {
      return product.storage.length > 0;
    }
    const s = product.storage;
    return Boolean(
      (s.type && String(s.type).trim() !== "" && s.type !== "—") ||
      (s.capacity !== undefined && s.capacity !== null && String(s.capacity).trim() !== "" && String(s.capacity) !== "—") ||
      (s.trigger !== undefined && s.trigger !== null && String(s.trigger).trim() !== "" && String(s.trigger) !== "—")
    );
  }, [product.storage]);

  const hasDimensions = React.useMemo(() => {
    if (!product.dimensions) return false;
    const d = product.dimensions;
    return Boolean(
      (d.weight && String(d.weight).trim() !== "" && d.weight !== "—") ||
      (d.length && String(d.length).trim() !== "" && d.length !== "—") ||
      (d.width && String(d.width).trim() !== "" && d.width !== "—") ||
      (d.height && String(d.height).trim() !== "" && d.height !== "—")
    );
  }, [product.dimensions]);

  const availableTabs = React.useMemo(() => {
    const list: Array<{ label: string; count: number }> = [];
    if (product.variants && product.variants.length > 0) {
      list.push({ label: "Variants", count: product.variants.length });
    }
    if (matchedPromos && matchedPromos.length > 0) {
      list.push({ label: "Promo", count: matchedPromos.length });
    }
    if (matchedGifts && matchedGifts.length > 0) {
      list.push({ label: "Gift", count: matchedGifts.length });
    }
    return list;
  }, [product.variants, matchedPromos, matchedGifts]);

  React.useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.some((t) => t.label === activeTab)) {
      setActiveTab(availableTabs[0].label);
    }
  }, [availableTabs, activeTab]);

  const getFormattedImgUrl = (url?: string) => {
    if (!url) return "";
    if (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("blob:") ||
      url.startsWith("data:")
    ) {
      return url;
    }
    const baseUrl = process.env.REACT_APP_BASE_URL || "";
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    return `${baseUrl}${cleanPath}`;
  };

  const imgUrl = getFormattedImgUrl(product.image);

  return (
    <div className="rounded border  border-dashed border-[#D2D2D2] shadow-sm hover:shadow-md transition overflow-hidden flex flex-col justify-between">
      {/* Header profile block */}
      <div className=" py-2 divide-y divide-dashed space-y-2">
        <div className="flex justify-between items-center px-4">
          <div className="flex flex-wrap gap-1.5">
            {product.features?.hasVariants && (
              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-700 border border-yellow-200/50">
                Variants
              </span>
            )}
            {product.features?.hasExpiry && (
              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200/50">
                Expiry
              </span>
            )}
            {product.features?.isDangerousGoods && (
              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-700 border border-rose-200/50">
                Dangerous
              </span>
            )}
            {customsFields.hasAny && (
              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200/50">
                Customs
              </span>
            )}
            {(matchedPromos.length > 0 || (product.features as any)?.hasPromo) && (
              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 border border-purple-200/50">
                Promo
              </span>
            )}
            {(matchedGifts.length > 0 || (product.features as any)?.hasGift) && (
              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-200/50">
                Gift
              </span>
            )}
          </div>
          <div className="flex items-center  gap-1 justify-end">
            <button
              type="button"
              onClick={() =>
                onToggleStatus && onToggleStatus(product.id, product.status)
              }
              className={`px-2.5 py-0.5 rounded border text-[10px] font-semibold capitalize transition ${isActive
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                  : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                }`}
              title="Click to toggle status"
            >
              {product.status || "ACTIVE"}
            </button>

            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(product.id)}
                className="p-1 text-rose-400 hover:text-rose-600 rounded-md hover:bg-rose-50 transition"
                title="Delete Product"
              >
                <IconTrash size={15} />
              </button>
            )}
          </div>
        </div>
        <div
          onDoubleClick={() => (onView ? onView(product) : onEdit && onEdit(product))}
          className="pt-1 flex gap-4 px-4 cursor-pointer hover:bg-slate-50/60 transition-colors rounded-lg py-1 select-none"
          title="Double click to view product details"
        >
          {imgUrl ? (
            <img
              src={imgUrl}
              alt={product.name}
              onError={(e) => {
                (e.target as HTMLElement).style.display = "none";
              }}
              className="w-20 h-20 rounded-xl object-contain bg-slate-50 border border-[#D2D2D2] shrink-0"
            />
          ) : (
            <div className="w-20 h-20 rounded-xl bg-slate-100 border border-[#D2D2D2] shrink-0 flex items-center justify-center text-slate-400">
              <IconGlobe size={28} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2">
              <div className="space-y-1.5">
                <h2 className="text-base text-[#212121] font-semibold">
                  SKU: {product.sku}
                </h2>
                <h3 className="text-sm font-medium text-[#212121] leading-tight">
                  {product.name}
                </h3>
                <p className="text-xs font-medium bg-slate-100 text-[#212121]/70 px-2 py-0.5 rounded">
                  {product.category}
                </p>
              </div>
              <div className="text-right shrink-0 space-y-1">
                {/* <div className="flex items-center gap-0.5 justify-end"> */}
                <span className="text-sm font-bold text-[#212121] block">
                  SAR {product.price}
                </span>

                {product.discount > 0 && (
                  <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-100">
                    (-SAR {product.discount})
                  </span>
                )}
                {/* </div> */}
              </div>
            </div>
          </div>
        </div>
        <div
          onDoubleClick={() => (onView ? onView(product) : onEdit && onEdit(product))}
          className="divide-y divide-dashed divide-gray-200 cursor-pointer hover:bg-slate-50/60 transition-colors pb-1 select-none"
          title="Double click to view product details"
        >
          {product.shortDesc && (
            <p className="text-[11px] text-[#212121]/70 mt-1 line-clamp-2 leading-relaxed px-4">
              <b>Short Description:</b> {product.shortDesc}
            </p>
          )}
          {product.longDesc && (
            <p className="text-[11px] text-[#212121]/70 mt-1 line-clamp-2 leading-relaxed px-4">
              <b>Long Description:</b> {product.longDesc}
            </p>
          )}
        </div>
      </div>

      {/* Specifications Body */}
      {isExpanded && (
        <div className="border-t border-dashed border-[#D2D2D2] transition-all duration-300">
        {/* Storage Profile & Measurements Profile */}
        {(hasStorage || hasDimensions) && (
          <div className="border border-dashed border-[#D2D2D2] w-full overflow-hidden">
            <div className={`grid ${hasStorage && hasDimensions ? "grid-cols-2 divide-x divide-dashed divide-[#D2D2D2]" : "grid-cols-1"} w-full items-stretch`}>
              {/* Storage */}
              {hasStorage && (
                <div className="w-full flex flex-col h-full">
                  <div className="bg-gray-50 px-3 py-1 border-b border-dashed border-[#D2D2D2] w-full">
                    <span className="text-xs font-semibold text-[#212121] block">
                      Storage
                    </span>
                  </div>
                  {Array.isArray(product.storage) ? (
                    product.storage.map((s: any, idx: number) => (
                      <div
                        key={idx}
                        className="grid grid-cols-3 text-center divide-x divide-dashed divide-[#D2D2D2] border-b border-dashed border-[#D2D2D2] last:border-0 w-full flex-1 items-stretch"
                      >
                        <div className="flex items-center gap-1.5 px-2 py-2.5 justify-center h-full">
                          <p className="text-[10px] font-medium text-[#212121]/70">Type:</p>
                          <p className="text-[10px] font-medium text-[#212121]">
                            {s?.type || s?.storageType || "BINS"}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 px-2 py-2.5 justify-center h-full">
                          <p className="text-[10px] font-medium text-[#212121]/70">Cap:</p>
                          <p className="text-[10px] font-medium text-[#212121]">
                            {s?.capacity ?? "—"}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 px-2 py-2.5 justify-center h-full">
                          <p className="text-[10px] text-rose-500">Tri:</p>
                          <p className="text-[10px] font-medium text-rose-600">
                            {s?.trigger ?? s?.minimumQuantity ?? "—"}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="grid grid-cols-3 text-center divide-x divide-dashed divide-[#D2D2D2] border-b border-dashed border-[#D2D2D2] last:border-0 w-full flex-1 items-stretch">
                      <div className="flex items-center gap-1.5 px-2 py-2.5 justify-center h-full">
                        <p className="text-[10px] font-medium text-[#212121]/70">Type:</p>
                        <p className="text-[10px] font-medium text-[#212121]">
                          {product.storage?.type || (product.storage as any)?.storageType || "BINS"}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 px-2 py-2.5 justify-center h-full">
                        <p className="text-[10px] font-medium text-[#212121]/70">Cap:</p>
                        <p className="text-[10px] font-medium text-[#212121]">
                          {product.storage?.capacity ?? "—"}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 px-2 py-2.5 justify-center h-full">
                        <p className="text-[10px] text-rose-500">Tri:</p>
                        <p className="text-[10px] font-medium text-rose-600">
                          {product.storage?.trigger ?? (product.storage as any)?.minimumQuantity ?? "—"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dimensions */}
              {hasDimensions && (
                <div className="w-full flex flex-col h-full">
                  <div className="bg-gray-50 px-3 py-1 border-b border-dashed border-[#D2D2D2] w-full">
                    <span className="text-xs font-semibold text-[#212121] block">
                      Dimensions
                    </span>
                  </div>
                  <div className="grid grid-cols-4 text-center divide-x divide-dashed divide-[#D2D2D2] w-full flex-1 items-stretch">
                    <div className="flex flex-col items-center justify-center py-2.5 px-1 h-full">
                      <p className="text-[10px] font-medium text-[#212121]/70 whitespace-nowrap">Weight:</p>
                      <p className="text-[10px] font-medium text-[#212121] mt-0.5 whitespace-nowrap">
                        {product.dimensions?.weight && product.dimensions.weight !== "—"
                          ? `${product.dimensions.weight} g`
                          : "—"}
                      </p>
                    </div>
                    <div className="flex flex-col items-center justify-center py-2.5 px-1 h-full">
                      <p className="text-[10px] font-medium text-[#212121]/70 whitespace-nowrap">Length:</p>
                      <p className="text-[10px] font-medium text-[#212121] mt-0.5 whitespace-nowrap">
                        {product.dimensions?.length && product.dimensions.length !== "—"
                          ? `${product.dimensions.length} cm`
                          : "—"}
                      </p>
                    </div>
                    <div className="flex flex-col items-center justify-center py-2.5 px-1 h-full">
                      <p className="text-[10px] font-medium text-[#212121]/70 whitespace-nowrap">Width:</p>
                      <p className="text-[10px] font-medium text-[#212121] mt-0.5 whitespace-nowrap">
                        {product.dimensions?.width && product.dimensions.width !== "—"
                          ? `${product.dimensions.width} cm`
                          : "—"}
                      </p>
                    </div>
                    <div className="flex flex-col items-center justify-center py-2.5 px-1 h-full">
                      <p className="text-[10px] font-medium text-[#212121]/70 whitespace-nowrap">Height:</p>
                      <p className="text-[10px] font-medium text-[#212121] mt-0.5 whitespace-nowrap">
                        {product.dimensions?.height && product.dimensions.height !== "—"
                          ? `${product.dimensions.height} cm`
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compliance settings */}
        {customsFields.hasAny && product.compliance && (
          <div className="border border-dashed border-[#D2D2D2] space-y-1.5">
            <div className="bg-gray-50 px-3 py-1 border-b border-dashed border-[#D2D2D2] flex justify-between items-center">
              <span className="text-xs font-semibold text-[#212121] block">
                Customs Details
              </span>
              {customsFields.hasHsCode && (
                <span className="text-[10px] text-indigo-600 font-bold tracking-wider">
                  HS CODE {product.compliance.hsCode}
                </span>
              )}
            </div>

            {(customsFields.hasRoute || customsFields.hasDuty || customsFields.hasVat || customsFields.hasLicense) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#212121]/70 px-3 py-1">
                {customsFields.hasRoute && (
                  <p className="text-[10px]">
                    Route:{" "}
                    <strong className="text-[#212121]">
                      {product.compliance.origin || "—"} → {product.compliance.destination || "—"}
                    </strong>
                  </p>
                )}
                {customsFields.hasDuty && (
                  <p className="text-[10px]">
                    Tariffs:{" "}
                    <strong className="text-emerald-600">
                      {product.compliance.duty}% Duty
                    </strong>
                  </p>
                )}
                {customsFields.hasVat && (
                  <p className="text-[10px]">
                    VAT:{" "}
                    <strong className="text-indigo-600">
                      {product.compliance.vat}% Tax
                    </strong>
                  </p>
                )}
                {customsFields.hasLicense && (
                  <p className="text-[10px]">
                    Licence:{" "}
                    <strong className="text-[#212121]">
                      {product.compliance.license}
                    </strong>
                  </p>
                )}
              </div>
            )}
            {customsFields.hasDocuments && (
              <div className="text-[10px] text-[#212121]/50 pt-1 border-t border-[#D2D2D2] px-3 py-2">
                <strong>Mandatory Documentation:</strong>{" "}
                {product.compliance.documents}
              </div>
            )}
          </div>
        )}

        {/* Variants Attributes */}
        {availableTabs.length > 0 && (
          <div className="border border-dashed border-[#D2D2D2] ">
            <div className="bg-gray-50 px-3 py-1  flex justify-between items-center">
              {activeTab === "Variants" && (
                <p className="text-xs font-semibold text-[#212121] block">
                  Variants Details ({product.variants?.length || 0})
                </p>
              )}
              {activeTab === "Promo" && (
                <p className="text-xs font-semibold text-[#212121] block">
                  Promo ({matchedPromos.length})
                </p>
              )}
              {activeTab === "Gift" && (
                <p className="text-xs font-semibold text-[#212121] block">
                  Gift ({matchedGifts.length})
                </p>
              )}
              <div className="flex items-center gap-2">
                {availableTabs.map((tab) => (
                  <button
                    key={tab.label}
                    onClick={() => setActiveTab(tab.label)}
                    className={`py-1 px-2.5 text-[10px]   font-semibold rounded transition-all flex items-center justify-center border shrink-0 flex-1 ${activeTab === tab.label
                        ? "bg-[#FBD037]/20 text-[#000000] shadow-sm border-[#FBD037]"
                        : "bg-slate-100 text-slate-600 border-slate-200"
                      }`}
                  >
                    <span className="whitespace-nowrap">
                      <T>{tab.label}</T>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-1 max-h-24 overflow-y-auto pr-1">
              {activeTab === "Variants" && product.variants && product.variants.length > 0 && (
                <ListView
                  tdClassName="rounded-r-none rounded-l-none text-left bg-transparent py-0"
                  mainDivClassName="bg-transparent rounded-b-3xl"
                  trClassName="hover:bg-[#FBD037]/20 "
                  mailDivClassName="p-0 bg-transparent rounded-b-3xl"
                  thClassName="border-t  text-left sticky top-0 z-20"
                  tbodyClass=""
                  data={product.variants}
                  columns={[
                    {
                      label: "#",
                      render: (_: any, i: number) => (
                        <div className="text-left text-[10px]">{i + 1}</div>
                      ),
                    },
                    {
                      label: "SKU",
                      render: (c: any) => (
                        <div className="text-left text-[10px]">
                          {c.sku || "—"}
                        </div>
                      ),
                    },
                    {
                      label: "Name",
                      render: (c: any) => (
                        <div className="text-left text-[10px]">
                          {c.name || "Variant"}
                        </div>
                      ),
                    },
                    {
                      label: "Option Value",
                      render: (c: any) => (
                        <div className="text-left text-[10px]">
                          {c.value || "—"}
                        </div>
                      ),
                    },
                    {
                      label: "Price (SAR)",
                      render: (c: any) => (
                        <div className="text-left text-[10px] flex items-center gap-1">
                          <img
                            src="/img/riyal-icon.svg"
                            className="w-2 h-2"
                            alt=""
                          />{" "}
                          {c.price}
                        </div>
                      ),
                    },
                  ]}
                />
              )}
              {activeTab === "Promo" && (
                <ListView
                  tdClassName="rounded-r-none rounded-l-none text-left bg-transparent py-0"
                  mainDivClassName="bg-transparent rounded-b-3xl"
                  trClassName="hover:bg-[#FBD037]/20 "
                  mailDivClassName="p-0 bg-transparent rounded-b-3xl"
                  thClassName="border-t  text-left sticky top-0 z-20"
                  tbodyClass=""
                  data={matchedPromos}
                  onRowClick={(item) => onEditPromo?.(item)}
                  columns={[
                    {
                      label: "#",
                      render: (_: any, i: number) => (
                        <div className="text-left text-[10px]">{i + 1}</div>
                      ),
                    },
                    {
                      label: "Promo SKU",
                      render: (c: any) => (
                        <div className="text-left text-[10px]">{c.sku}</div>
                      ),
                    },
                    {
                      label: "QTY",
                      render: (c: any) => {
                        const match = c.products?.find((item: any) => productSkus.includes(item.productSku));
                        return (
                          <div className="text-left text-[10px]">{match ? match.qty : 0}</div>
                        );
                      },
                    },
                    {
                      label: "Date",
                      render: (c: any) => (
                        <div className="text-left text-[10px]">
                          {c.startDate && c.endDate ? `${c.startDate} - ${c.endDate}` : c.startDate || c.endDate || "—"}
                        </div>
                      ),
                    },
                    {
                      label: "Status",
                      render: (c: any) => (
                        <div onClick={(e) => e.stopPropagation()}>
                          <ToggleButton
                            checked={c.status === "ACTIVE"}
                            onChange={() => onTogglePromoStatus?.(c.id, c.status)}
                          />
                        </div>
                      ),
                    },
                  ]}
                />
              )}
              {activeTab === "Gift" && (
                <div className="overflow-x-auto max-h-60 overflow-y-auto">
                  {matchedGifts.length === 0 ? (
                    <div className="text-center py-3 text-[10px] text-gray-400">
                      No gift rules attached to this product.
                    </div>
                  ) : (
                    <table className="w-full text-left border-collapse text-[10px]">
                      <thead>
                        <tr className="border-t border-b border-gray-200 bg-gray-50/80 text-[10px] text-gray-600 font-semibold sticky top-0 z-20">
                          <th className="py-1.5 px-2 w-7">#</th>
                          <th className="py-1.5 px-2">Item / SKU</th>
                          <th className="py-1.5 px-2 w-16 text-center">QTY</th>
                          <th className="py-1.5 px-2 whitespace-nowrap">Date</th>
                          <th className="py-1.5 px-2 text-center w-12">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {matchedGifts.map((c: any, index: number) => {
                          const giftList = Array.isArray(c.giftItems) && c.giftItems.length > 0
                            ? c.giftItems
                            : [{ sku: c.giftProductSku || c.giftSku, qty: c.giftQty || 1 }];

                          return (
                            <React.Fragment key={c.id || index}>
                              {/* 1 Row for Main Item */}
                              <tr
                                onClick={() => onEditGift?.(c)}
                                className="hover:bg-[#FBD037]/20 transition-colors cursor-pointer bg-amber-50/40 font-medium"
                              >
                                <td className="py-1.5 px-2 text-gray-500 font-medium align-top">
                                  {index + 1}
                                </td>
                                <td className="py-1.5 px-2 align-top">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono font-bold text-slate-900">
                                      {c.mainProductSku || product.sku}
                                    </span>
                                    <span className="text-[8px] font-bold uppercase tracking-wider text-amber-800 bg-amber-200/70 px-1.5 py-0.2 rounded">
                                      Main
                                    </span>
                                  </div>
                                </td>
                                <td className="py-1.5 px-2 text-center align-top">
                                  <span className="font-bold text-amber-900 bg-amber-100 px-1.5 py-0.5 rounded text-[9px]">
                                    {c.mainQty || 1}
                                  </span>
                                </td>
                                <td className="py-1.5 px-2 text-gray-600 whitespace-nowrap align-top text-[9px]">
                                  {c.startDate && c.endDate ? `${c.startDate} - ${c.endDate}` : c.startDate || c.endDate || "—"}
                                </td>
                                <td className="py-1.5 px-2 text-center align-top" onClick={(e) => e.stopPropagation()}>
                                  <ToggleButton
                                    checked={c.status === "ACTIVE"}
                                    onChange={() => onToggleGiftStatus?.(c.id, c.status || "ACTIVE")}
                                  />
                                </td>
                              </tr>

                              {/* Sub Rows for Gift Items under this Main Item */}
                              {giftList.map((g: any, gIdx: number) => (
                                <tr
                                  key={gIdx}
                                  onClick={() => onEditGift?.(c)}
                                  className="hover:bg-[#FBD037]/20 transition-colors cursor-pointer bg-white/70"
                                >
                                  <td className="py-1 px-2 text-center text-slate-300">
                                    <span className="inline-block w-1 h-1 rounded-full bg-emerald-400"></span>
                                  </td>
                                  <td className="py-1 px-2 pl-4">
                                    <div className="flex items-center gap-1.5 text-emerald-800">
                                      <span className="text-slate-400 text-[11px] font-bold">↳</span>
                                      <span className="font-mono font-medium">{g.sku || "—"}</span>
                                      <span className="text-[8px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-1.5 py-0.2 rounded">
                                        Gift
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-1 px-2 text-center">
                                    <span className="font-bold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded text-[9px]">
                                      +{g.qty || 1}
                                    </span>
                                  </td>
                                  <td className="py-1 px-2 text-slate-400 text-[9px]">—</td>
                                  <td className="py-1 px-2"></td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
};
